import { useMemo } from "react";

import TriangleUp from "@/assets/icons/triangle-up.svg";
import { BACKEND_MENTION_TIMING_ADJUSTMENT } from "@/constants";
import { useEpisode } from "@/containers/Episode/context";
import { useLocalAudio } from "@/containers/Episode/LocalAudioContext";
import { TimeCluster } from "@/containers/Episode/utils/clusterTimes";
import { AudioMention } from "@/types/Mention";
import { cx, durationFromSeconds } from "@/utils";
import Tooltip from "@components/Tooltip";
import Typography from "@components/Typography";

import styles from "./Timeline.module.css";

type TimelineMarkerProps = {
  mention: AudioMention;
  active: boolean;
  onClick: () => void;
} & React.HTMLProps<HTMLDivElement>;

const TimelineMarker: React.FunctionComponent<TimelineMarkerProps> = ({
  mention,
  active,
  hidden,
  onClick,
  ...props
}: TimelineMarkerProps) => {
  const { playing, audio } = useLocalAudio();
  const { sentimentActive } = useEpisode();
  return (
    <Tooltip content={durationFromSeconds(mention.context_start_timing)}>
      <div
        {...props}
        style={{
          left: `${
            ((mention.context_start_timing +
              BACKEND_MENTION_TIMING_ADJUSTMENT) /
              mention.total_length) *
            100
          }%`,
        }}
        className={cx(styles.marker, active ? styles.active : styles.inactive, {
          [styles.hidden]: hidden,
        })}
        onClick={(e) => {
          // start playback only when a player exists
          if (playing && audio) {
            audio.play(mention.context_start_timing);
          }
          onClick(e); // always notify parent
        }}
      >
        <span
          className={cx(styles.markerInner, {
            [styles.positive]:
              sentimentActive && mention.sentiment === "positive",
            [styles.neutral]:
              sentimentActive && mention.sentiment === "neutral",
            [styles.negative]:
              sentimentActive && mention.sentiment === "negative",
          })}
        >
          <TriangleUp stroke="currentColor" />
        </span>
      </div>
    </Tooltip>
  );
};

type TimelineProps = {
  visible: AudioMention;
  onMarkerClick: (s: AudioMention) => void;
};

const createCluster = (clusters: TimeCluster[], mentions: AudioMention[]) => {
  const totalLength = mentions[0].total_length;
  const clusterSizes: Array<{ width: number; left: number }> = [];
  clusters.forEach(([start, end]) => {
    // never mind the the loners right now.
    if (end !== undefined && start !== undefined) {
      const chunkWidth = (Math.min(end, totalLength) - start) / totalLength;
      const chunkStart = start / totalLength;

      clusterSizes.push({
        width: chunkWidth * 100,
        left: chunkStart * 100,
      });
    }
  });
  return clusterSizes;
};

const Timeline = ({ visible, onMarkerClick }: TimelineProps) => {
  const { clusters, audioMentions, removedMentionIds } = useEpisode();
  const { currentTime } = useLocalAudio({ currentTime: true });

  const clusterSizes = useMemo(
    () => createCluster(clusters, audioMentions),
    [clusters, audioMentions]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.inner}>
        <Typography block variant="small" className={styles.timestamp}>
          {durationFromSeconds(
            currentTime || audioMentions?.[0].context_start_timing || 0
          )}
        </Typography>
        <div className={styles.timeline}>
          {clusterSizes.map((c, i) => (
            <div
              key={i}
              className={styles.cluster}
              style={{ left: `${c.left}%`, width: `${c.width}%` }}
            />
          ))}
          <div className={styles.markers}>
            {audioMentions.map((mention) => (
              <TimelineMarker
                data-testid="mention-marker"
                mention={mention}
                data-mention-id={mention.public_id}
                key={mention.public_id}
                active={visible?.public_id === mention.public_id}
                hidden={removedMentionIds.includes(mention.public_id)}
                onClick={() => {
                  onMarkerClick(mention);
                }}
              />
            ))}
          </div>
        </div>
        <Typography
          block
          variant="small"
          className={cx(styles.timestamp, styles.end)}
        >
          {durationFromSeconds(audioMentions[0].total_length) || "unknown"}
        </Typography>
      </div>
    </div>
  );
};

export default Timeline;
